
'use client';

import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Log } from '@/lib/logging/types';
import { getLogs as fetchLogs, clearLogs as clearServerLogs, logRequest, updateLog as updateServerLog } from '@/lib/logging/server-logging';
import { ALL_AGENT_IDS } from '@/ai/agents/definitions';

// Module-level flag to ensure initialization only happens once per app load.
let hasInitialized = false;
const DEFAULT_LOGS_PER_PAGE = 25;

// Track start times for duration calculation
const logStartTimes = new Map<string, number>();

export interface LogContextType {
  logs: Log[];
  isInitialized: boolean;
  createLog: (logData: Omit<Log, 'id' | 'timestamp' | 'status'>) => Promise<string>;
  updateLog: (logId: string, updates: Partial<Log>) => Promise<void>;
  clearLogs: () => void;
  refreshLogs: () => Promise<void>;
  loadMoreLogs: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  selectedAgents: string[];
  setSelectedAgents: (agents: string[]) => void;
  availableAgents: string[];
  projectIdFilter: string | undefined;
  setProjectIdFilter: (projectId: string | undefined) => void;
  logsPerPage: number;
  setLogsPerPage: (n: number) => void;
  scope: 'project' | 'all' | 'unset';
  setScope: (scope: 'project' | 'all' | 'unset') => void;
}

export const LogContext = createContext<LogContextType | undefined>(undefined);

export function LogProvider({ children }: { children: ReactNode }) {
  const [logs, setLogs] = useState<Log[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [projectIdFilter, setProjectIdFilter] = useState<string | undefined>(undefined);
  const [logsPerPage, setLogsPerPage] = useState<number>(DEFAULT_LOGS_PER_PAGE);
  const [scope, setScope] = useState<'project' | 'all' | 'unset'>('unset');

  // Use all available agents from the system, not just the ones in loaded logs
  const availableAgents = ALL_AGENT_IDS;

  const refreshLogs = useCallback(async () => {
    try {
      console.log('[LogProvider] Refreshing logs (page 1)...', projectIdFilter ? `Project: ${projectIdFilter}` : 'All projects', `pageSize: ${logsPerPage}`);
      // When refreshing, fetch without agent filter to get all logs, but apply project filter
      const serverLogs = await fetchLogs(1, undefined, projectIdFilter, logsPerPage);
      setLogs(serverLogs);
      setHasMore(serverLogs.length === logsPerPage);
      setCurrentPage(1);
      console.log(`[LogProvider] Refreshed with ${serverLogs.length} logs.`);
    } catch (error) {
      console.error('[LogProvider] Error refreshing logs from server:', error);
    }
  }, [projectIdFilter, logsPerPage]);

  const loadMoreLogs = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
        // Apply filter when loading more logs if agents are selected
        const filterToApply = selectedAgents.length > 0 ? selectedAgents : undefined;
        const newLogs = await fetchLogs(nextPage, filterToApply, projectIdFilter, logsPerPage);
        setLogs(prevLogs => [...prevLogs, ...newLogs]);
        setHasMore(newLogs.length === logsPerPage);
        setCurrentPage(nextPage);
    } catch (error) {
        console.error('[LogProvider] Error loading more logs:', error);
    } finally {
        setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    // Refresh logs when a consumer has declared scope (project-scoped or explicitly all-projects).
    // This avoids a wasted fetch on mount before any consumer has configured the viewer.
    if (scope !== 'unset') {
      refreshLogs().then(() => setIsInitialized(true));
    }
  }, [refreshLogs, scope]);
  
  const createLog = async (logData: Omit<Log, 'id' | 'timestamp' | 'status'>): Promise<string> => {
    console.log('[LogProvider] createLog called for flow:', logData.flow);
    const tempId = crypto.randomUUID(); // Generate a temporary, unique ID for the optimistic update

    // Record start time for duration tracking
    const startTime = performance.now();

    const newLogEntry: Log = {
      id: tempId,
      ...logData,
      timestamp: new Date().toISOString(),
      status: 'pending',
    };

    // Optimistically update UI with the temporary entry
    setLogs(prevLogs => [newLogEntry, ...prevLogs]);

    // Asynchronously get the real ID from the server
    const realLogId = await logRequest(logData.flow, logData.request);
    console.log(`[LogProvider] createLog received real ID from server: ${realLogId}`);

    // Store start time with the real log ID
    logStartTimes.set(realLogId, startTime);

    // Update the local state to replace the temporary entry with the one having the correct ID
    setLogs(prevLogs => prevLogs.map(log =>
      log.id === tempId ? { ...log, id: realLogId } : log
    ));

    console.log('[LogProvider] createLog finished.');
    return realLogId;
  };
  
  const updateLog = async (logId: string, updates: Partial<Log>) => {
    console.log(`[LogProvider] updateLog called for ID: ${logId} with status: ${updates.status}`);

    // Calculate total duration if this is a final update (success or error)
    if ((updates.status === 'success' || updates.status === 'error') && logStartTimes.has(logId)) {
      const startTime = logStartTimes.get(logId)!;
      const endTime = performance.now();
      updates.totalDurationMs = Math.round(endTime - startTime);

      // Clean up the start time entry
      logStartTimes.delete(logId);

      console.log(`[LogProvider] Total duration for ${logId}: ${updates.totalDurationMs}ms`);
    }

    // Optimistically update UI
    setLogs(prevLogs => prevLogs.map(log =>
      log.id === logId ? { ...log, ...updates } : log
    ));

    await updateServerLog(logId, updates);
    console.log(`[LogProvider] updateLog finished for ID: ${logId}`);
  };
  
  const clearLogs = async () => {
    console.log('[LogProvider] clearLogs called.');
    // Optimistically update UI
    setLogs([]);
    setHasMore(true);
    setCurrentPage(1);
    await clearServerLogs(projectIdFilter);
    console.log('[LogProvider] clearLogs finished.');
  };

  const value = {
    logs,
    isInitialized,
    createLog,
    updateLog,
    clearLogs,
    refreshLogs,
    loadMoreLogs,
    hasMore,
    isLoadingMore,
    selectedAgents,
    setSelectedAgents,
    availableAgents,
    projectIdFilter,
    setProjectIdFilter,
    logsPerPage,
    setLogsPerPage,
    scope,
    setScope,
  };

  return <LogContext.Provider value={value}>{children}</LogContext.Provider>;
}
